import { computed, onUnmounted, reactive, Ref, ref, toRefs, unref } from "@vue/composition-api";
import {
  LiveChatJoinResponse,
  LivechatSubjectType,
  LiveChatUser,
  LiveChatUserBroadcastingEvent,
  LiveChatUserUpdateAction,
} from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import { getNow, parseISO } from "@/lib/time";
import { ERRORS, EVENTS } from "@/const";
import useStreamLogger from "./useStreamLogger";

interface State {
  joinData: LiveChatJoinResponse | null;
  loading: boolean;
  errorData: Record<string, any> | null;
}

type UserBroadcastingCB = (data: LiveChatUserBroadcastingEvent) => void;
type UserListUpdatedCB = (data: { action: LiveChatUserUpdateAction; user: LiveChatUser }) => void;

export default function useLiveChat({
  subject,
  referrerId,
}: {
  subject: Ref<Subject<LivechatSubjectType>>;
  referrerId?: Ref<string | undefined>;
}) {
  const state = reactive<State>({
    joinData: null,
    loading: false,
    errorData: null,
  });
  const clientServerTimeDiff = ref(0);
  const keepAliveStart = ref(false);
  const isBlockedForSession = ref(false);
  let onUserBroadcastingCB: UserBroadcastingCB = () => {};
  let userBroadcastingUnsubscribe = () => {};
  let onUserListUpdatedCB: UserListUpdatedCB = () => {};
  let userListUpdateUnsubscribe = () => {};

  const token = computed(() => state.joinData?.token ?? "");

  const setClientServerTimeDiff = (serverTime: Date) => {
    clientServerTimeDiff.value = getNow().getTime() - serverTime.getTime();
  };

  const updateUserList = (users: LiveChatUser[]) => {
    if (!state.joinData) return;
    state.joinData = {
      ...state.joinData,
      users,
    };
  };

  const updateMe = (me: LiveChatUser) => {
    if (!state.joinData) return;
    state.joinData = {
      ...state.joinData,
      me,
    };
  };

  const getLoggerContext = () => {
    return {
      joinData: state.joinData,
      errorData: state.errorData,
    };
  };
  const { businessLogicLog, collectErrorLogs } = useStreamLogger("[USE LIVECHAT]", getLoggerContext);

  const endChat = async () => {
    userBroadcastingUnsubscribe();
    userListUpdateUnsubscribe();
    keepAliveStart.value = false;

    if (!state.joinData) return;
    businessLogicLog("End chat " + subject.value.subjectType);
    await providers.LivechatsProvider.end({ token: state.joinData.token });
    state.joinData = null;
  };

  const setOnUserBroadcastingCB = (cb: UserBroadcastingCB) => (onUserBroadcastingCB = cb);
  const subscribeUserBroadcasting = async () => {
    businessLogicLog("Subscribe users to the broadcasting " + subject.value.subjectType);
    if (!token.value) throw new Error("Empty token");
    const subscription = await providers.LivechatsProvider.subscribeUserBroadcasting(
      unref(token),
      onUserBroadcastingCB
    );
    userBroadcastingUnsubscribe = () => subscription.unsubscribe();
  };

  const setOnUserListUpdatedCB = (cb: UserListUpdatedCB) => (onUserListUpdatedCB = cb);

  const subscribeUserListUpdated = async () => {
    businessLogicLog("Update subscription list of users");
    const subscription = await providers.LivechatsProvider.subscribeUserUpdate(
      unref(token),
      (data: { action: LiveChatUserUpdateAction; user: LiveChatUser }) => {
        if (!state.joinData) return;
        onUserListUpdatedCB(data);
        if (data.action === LiveChatUserUpdateAction.DELETE) {
          updateUserList(state.joinData.users.filter(i => i.id !== data.user.id));
        }
        if (data.action === LiveChatUserUpdateAction.UPDATE) {
          if (data.user.id === state.joinData.me.id) {
            updateMe(data.user);
          }
          if (state.joinData.users.find(i => data.user.id === i.id)) {
            updateUserList(state.joinData.users.map(i => (i.id === data.user.id ? data.user : i)));
          } else {
            emitter.emit(EVENTS.STREAM_USER_JOIN, { user: data.user, threadId: state.joinData.threadId });
            updateUserList(state.joinData.users.concat([data.user]));
          }
        }
      }
    );
    userListUpdateUnsubscribe = () => subscription.unsubscribe();
  };

  const keepAlive = async (token: string): Promise<void> => {
    await new Promise(r => setTimeout(r, 1000));
    if (!keepAliveStart.value) return;
    try {
      await providers.LivechatsProvider.keepalive({ token });
    } catch (e) {
      collectErrorLogs("Keep alive " + subject.value.subjectType, e);
    }
    return keepAlive(token);
  };

  const getUserList = async () => {
    const response = await providers.LivechatsProvider.join({
      data: {
        subject: subject.value,
        referrerId: referrerId?.value,
      },
    });
    updateUserList(response.users);
  };

  const joinChat = async (connectionDelay = 0) => {
    try {
      state.loading = true;
      keepAliveStart.value = true;
      connectionDelay && (await new Promise(r => setTimeout(r, connectionDelay)));
      businessLogicLog("Create chat " + subject.value.subjectType);
      providers.LivechatsProvider.setReceiveErrors(false);
      state.joinData = await providers.LivechatsProvider.join({
        data: {
          subject: subject.value,
          referrerId: referrerId?.value,
        },
      });
      setClientServerTimeDiff(parseISO(state.joinData.serverTime));
      await subscribeUserListUpdated();
      await subscribeUserBroadcasting();
      keepAlive(state.joinData.token);
      getUserList();
    } catch (e) {
      handleRequestError(e, [
        {
          comparator: e => e.details?.messageTemplate === ERRORS.NO_EVENT_TICKET,
          onSuccessCB: e => {
            const err = e?.details?.data ?? "";
            state.errorData = typeof err === "string" ? JSON.parse(err) : "";
            throw e;
          },
        },
        {
          comparator: e => e.details?.messageTemplate === ERRORS.BLOCKED_FOR_LIVE_SESSION,
          onSuccessCB: () => {
            isBlockedForSession.value = true;
          },
        },
      ]);
      collectErrorLogs("Create chat " + subject.value.subjectType, e);
    } finally {
      state.loading = false;
    }
  };

  onUnmounted(() => {
    endChat().then();
  });

  return {
    ...toRefs(state),
    setOnUserListUpdatedCB,
    setOnUserBroadcastingCB,
    clientServerTimeDiff,
    isBlockedForSession,
    joinChat,
    endChat,
  };
}
